import { useEffect, useState } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import { Card, CardContent, CardHeader, CardTitle } from '../components/ui/card';
import { Button } from '../components/ui/button';
import { Input } from '../components/ui/input';
import { Label } from '../components/ui/label';
import { Badge } from '../components/ui/badge';
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
  DialogTrigger,
} from '../components/ui/dialog';
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from '../components/ui/select';
import {
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
} from '../components/ui/table';
import { Checkbox } from '../components/ui/checkbox';
import { ArrowLeft, Plus, Search, UserPlus, Trash2, Eye, Target, CheckCircle } from 'lucide-react';
import { toast } from 'sonner';
import { format } from 'date-fns';
import { usePermissions } from '../context/PermissionsContext';
import { logger } from '../utils/logger';

const API_URL = process.env.REACT_APP_BACKEND_URL;

const CAMPAIGN_TYPES = [
  { value: 'always_on_acquisition', label: 'Always-On Acquisition' },
  { value: 'monthly_office_event', label: 'Monthly Office Event' },
  { value: 'sales_week', label: 'Sales Week' },
];

const MEMBER_STATUSES = [
  { value: 'sent', label: 'Sent' },
  { value: 'responded', label: 'Responded' },
  { value: 'rsvp_yes', label: 'RSVP Yes' },
  { value: 'rsvp_no', label: 'RSVP No' },
  { value: 'attended', label: 'Attended' },
  { value: 'no_show', label: 'No Show' },
  { value: 'converted_to_deal', label: 'Converted to Deal' },
  { value: 'closed_won', label: 'Closed Won' },
];

const getTypeLabel = (type) => {
  const found = CAMPAIGN_TYPES.find(t => t.value === type);
  return found ? found.label : type;
};

const getStatusLabel = (status) => {
  const found = MEMBER_STATUSES.find(s => s.value === status);
  return found ? found.label : status;
};

const getStatusBadgeColor = (status) => {
  switch (status) {
    case 'sent': return 'bg-gray-100 text-gray-800';
    case 'responded': return 'bg-blue-100 text-blue-800';
    case 'rsvp_yes': return 'bg-green-100 text-green-800';
    case 'rsvp_no': return 'bg-red-100 text-red-800';
    case 'attended': return 'bg-emerald-100 text-emerald-800';
    case 'no_show': return 'bg-orange-100 text-orange-800';
    case 'converted_to_deal': return 'bg-purple-100 text-purple-800';
    case 'closed_won': return 'bg-gold/20 text-gold-dark';
    default: return 'bg-gray-100 text-gray-800';
  }
};

export default function CampaignDetailPage() {
  const { campaignId } = useParams();
  const navigate = useNavigate();
  const [campaign, setCampaign] = useState(null);
  const [members, setMembers] = useState([]);
  const [leads, setLeads] = useState([]);
  const [leadsInCampaign, setLeadsInCampaign] = useState(new Set());
  const [loading, setLoading] = useState(true);
  const [searchQuery, setSearchQuery] = useState('');
  const [statusFilter, setStatusFilter] = useState('all');
  const [isAddLeadOpen, setIsAddLeadOpen] = useState(false);
  const [selectedLeadId, setSelectedLeadId] = useState('');
  const [leadSearchQuery, setLeadSearchQuery] = useState('');
  
  const { hasPermission, role, user } = usePermissions();
  const canAddOwn = hasPermission('campaign_members_add_own');
  const canAddAny = hasPermission('campaign_members_add_any');
  const canUpdateOwn = hasPermission('campaign_members_update_own');
  const canUpdateAny = hasPermission('campaign_members_update_any');
  const canRemoveOwn = hasPermission('campaign_members_remove_own');
  const canRemoveAny = hasPermission('campaign_members_remove_any');
  const isReadOnly = role === 'top_management' || role === 'finance';

  useEffect(() => {
    fetchCampaign();
    fetchMembers();
    fetchLeads();
  }, [campaignId]); // eslint-disable-line react-hooks/exhaustive-deps

  const fetchCampaign = async () => {
    try {
      const response = await fetch(`${API_URL}/api/campaigns/${campaignId}`, { credentials: 'include' });
      if (response.ok) {
        const data = await response.json();
        setCampaign(data);
      } else {
        toast.error('Campaign not found');
        navigate('/campaigns');
      }
    } catch (error) {
      toast.error('Failed to fetch campaign');
    }
  };

  const fetchMembers = async () => {
    try {
      const response = await fetch(`${API_URL}/api/campaigns/${campaignId}/members`, { credentials: 'include' });
      if (response.ok) {
        const data = await response.json();
        setMembers(data);
        setLeadsInCampaign(new Set(data.map(m => m.lead_id)));
      }
    } catch (error) {
      toast.error('Failed to fetch campaign members');
    } finally {
      setLoading(false);
    }
  };

  const fetchLeads = async () => {
    try {
      const response = await fetch(`${API_URL}/api/leads`, { credentials: 'include' });
      if (response.ok) {
        const data = await response.json();
        setLeads(data);
      }
    } catch (error) {
      logger.error('Failed to fetch leads');
    }
  };

  const handleAddLead = async () => {
    if (!selectedLeadId) {
      toast.error('Please select a lead');
      return;
    }
    
    try {
      const response = await fetch(`${API_URL}/api/campaigns/${campaignId}/members`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        credentials: 'include',
        body: JSON.stringify({
          campaign_id: campaignId,
          lead_id: selectedLeadId,
          status: 'sent'
        })
      });
      
      if (response.ok) {
        toast.success('Lead added to campaign');
        setIsAddLeadOpen(false);
        setSelectedLeadId('');
        fetchMembers();
      } else {
        const error = await response.json();
        toast.error(error.detail || 'Failed to add lead');
      }
    } catch (error) {
      toast.error('Failed to add lead');
    }
  };

  const handleUpdateMemberStatus = async (memberId, newStatus) => {
    try {
      const response = await fetch(`${API_URL}/api/campaigns/${campaignId}/members/${memberId}`, {
        method: 'PUT',
        headers: { 'Content-Type': 'application/json' },
        credentials: 'include',
        body: JSON.stringify({ status: newStatus })
      });
      
      if (response.ok) {
        toast.success('Status updated');
        fetchMembers();
      } else {
        const error = await response.json();
        toast.error(error.detail || 'Failed to update status');
      }
    } catch (error) {
      toast.error('Failed to update status');
    }
  };

  const handleToggleSeatConfirmed = async (memberId, currentValue) => {
    try {
      const response = await fetch(`${API_URL}/api/campaigns/${campaignId}/members/${memberId}`, {
        method: 'PUT',
        headers: { 'Content-Type': 'application/json' },
        credentials: 'include',
        body: JSON.stringify({ seat_confirmed: !currentValue })
      });
      
      if (response.ok) {
        toast.success('Seat confirmation updated');
        fetchMembers();
      } else {
        const error = await response.json();
        toast.error(error.detail || 'Failed to update');
      }
    } catch (error) {
      toast.error('Failed to update');
    }
  };

  const handleRemoveMember = async (memberId) => {
    if (!window.confirm('Are you sure you want to remove this lead from the campaign?')) return;
    
    try {
      const response = await fetch(`${API_URL}/api/campaigns/${campaignId}/members/${memberId}`, {
        method: 'DELETE',
        credentials: 'include'
      });
      
      if (response.ok) {
        toast.success('Lead removed from campaign');
        fetchMembers();
      } else {
        const error = await response.json();
        toast.error(error.detail || 'Failed to remove lead');
      }
    } catch (error) {
      toast.error('Failed to remove lead');
    }
  };

  const canModifyMember = (member) => {
    const lead = leads.find(l => l.lead_id === member.lead_id);
    if (!lead) return false;
    const isOwnLead = lead.assigned_to === user?.user_id || lead.created_by === user?.user_id;
    return isOwnLead ? canUpdateOwn : canUpdateAny;
  };

  const canRemoveMember = (member) => {
    const lead = leads.find(l => l.lead_id === member.lead_id);
    if (!lead) return false;
    const isOwnLead = lead.assigned_to === user?.user_id || lead.created_by === user?.user_id;
    return isOwnLead ? canRemoveOwn : canRemoveAny;
  };

  // Filter leads that can be added (not already in campaign)
  const availableLeads = leads.filter(lead => {
    const notInCampaign = !leadsInCampaign.has(lead.lead_id);
    const matchesSearch = `${lead.first_name} ${lead.last_name}`.toLowerCase().includes(leadSearchQuery.toLowerCase());
    
    // Check if user can add this lead
    const isOwnLead = lead.assigned_to === user?.user_id || lead.created_by === user?.user_id;
    const canAddThisLead = isOwnLead ? canAddOwn : canAddAny;
    
    return notInCampaign && matchesSearch && canAddThisLead;
  });

  const filteredMembers = members.filter(member => {
    const lead = leads.find(l => l.lead_id === member.lead_id);
    const leadName = lead ? `${lead.first_name} ${lead.last_name}` : '';
    const matchesSearch = leadName.toLowerCase().includes(searchQuery.toLowerCase());
    const matchesStatus = statusFilter === 'all' || member.status === statusFilter;
    return matchesSearch && matchesStatus;
  });

  // Calculate stats
  const stats = {
    total: members.length,
    rsvp_yes: members.filter(m => m.status === 'rsvp_yes').length,
    attended: members.filter(m => m.status === 'attended').length,
    converted: members.filter(m => m.status === 'converted_to_deal' || m.status === 'closed_won').length,
  };

  if (loading) {
    return (
      <div className="flex items-center justify-center h-64">
        <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-gold"></div>
      </div>
    );
  }

  if (!campaign) {
    return null;
  }

  return (
    <div className="space-y-6 animate-fade-in" data-testid="campaign-detail-page">
      {/* Header */}
      <div className="flex items-center gap-4">
        <Button variant="ghost" size="icon" onClick={() => navigate('/campaigns')} data-testid="back-btn">
          <ArrowLeft size={20} />
        </Button>
        <div className="flex-1">
          <h1 className="font-heading text-3xl font-semibold text-slate-900">{campaign.name}</h1>
          <div className="flex items-center gap-3 mt-1">
            <Badge className="bg-purple-100 text-purple-800">{getTypeLabel(campaign.type)}</Badge>
            {campaign.start_date && (
              <span className="text-sm text-slate-500">
                {format(new Date(campaign.start_date), 'MMM d, yyyy')}
                {campaign.end_date && ` - ${format(new Date(campaign.end_date), 'MMM d, yyyy')}`}
              </span>
            )}
          </div>
        </div>
        {(canAddOwn || canAddAny) && (
          <Dialog open={isAddLeadOpen} onOpenChange={setIsAddLeadOpen}>
            <DialogTrigger asChild>
              <Button className="btn-accent" data-testid="add-lead-btn">
                <UserPlus size={20} className="mr-2" />
                Add Lead
              </Button>
            </DialogTrigger>
            <DialogContent className="max-w-md">
              <DialogHeader>
                <DialogTitle className="font-heading text-xl">Add Lead to Campaign</DialogTitle>
              </DialogHeader>
              <div className="space-y-4">
                <div>
                  <Label>Search Leads</Label>
                  <Input
                    placeholder="Search by name..."
                    value={leadSearchQuery}
                    onChange={(e) => setLeadSearchQuery(e.target.value)}
                    className="input-goldsands"
                    data-testid="lead-search"
                  />
                </div>
                <div>
                  <Label>Select Lead</Label>
                  <Select value={selectedLeadId} onValueChange={setSelectedLeadId}>
                    <SelectTrigger data-testid="lead-select">
                      <SelectValue placeholder="Select a lead..." />
                    </SelectTrigger>
                    <SelectContent>
                      {availableLeads.length === 0 ? (
                        <SelectItem value="none" disabled>No available leads</SelectItem>
                      ) : (
                        availableLeads.map(lead => (
                          <SelectItem key={lead.lead_id} value={lead.lead_id}>
                            {lead.first_name} {lead.last_name} {lead.email ? `(${lead.email})` : ''}
                          </SelectItem>
                        ))
                      )}
                    </SelectContent>
                  </Select>
                </div>
                <div className="flex justify-end gap-3 pt-4">
                  <Button variant="outline" onClick={() => { setIsAddLeadOpen(false); setSelectedLeadId(''); setLeadSearchQuery(''); }}>
                    Cancel
                  </Button>
                  <Button className="btn-accent" onClick={handleAddLead} data-testid="add-lead-submit">
                    Add to Campaign
                  </Button>
                </div>
              </div>
            </DialogContent>
          </Dialog>
        )}
      </div>

      {/* Read-only banner */}
      {isReadOnly && (
        <div className="bg-blue-50 border border-blue-200 rounded-lg p-4 flex items-center gap-3">
          <Eye className="text-blue-600" size={20} />
          <span className="text-blue-800">You have read-only access to this campaign.</span>
        </div>
      )}

      {/* Stats Cards */}
      <div className="grid grid-cols-2 md:grid-cols-4 gap-4">
        <Card className="card-goldsands">
          <CardContent className="p-4 text-center">
            <p className="text-2xl font-bold text-slate-900">{stats.total}</p>
            <p className="text-sm text-slate-500">Total Members</p>
          </CardContent>
        </Card>
        <Card className="card-goldsands">
          <CardContent className="p-4 text-center">
            <p className="text-2xl font-bold text-green-600">{stats.rsvp_yes}</p>
            <p className="text-sm text-slate-500">RSVP Yes</p>
          </CardContent>
        </Card>
        <Card className="card-goldsands">
          <CardContent className="p-4 text-center">
            <p className="text-2xl font-bold text-emerald-600">{stats.attended}</p>
            <p className="text-sm text-slate-500">Attended</p>
          </CardContent>
        </Card>
        <Card className="card-goldsands">
          <CardContent className="p-4 text-center">
            <p className="text-2xl font-bold text-gold">{stats.converted}</p>
            <p className="text-sm text-slate-500">Converted</p>
          </CardContent>
        </Card>
      </div>

      {/* Description */}
      {campaign.description && (
        <Card className="card-goldsands">
          <CardContent className="p-4">
            <p className="text-slate-600">{campaign.description}</p>
          </CardContent>
        </Card>
      )}

      {/* Filters */}
      <Card className="card-goldsands">
        <CardContent className="p-4">
          <div className="flex flex-col md:flex-row gap-4">
            <div className="flex-1 relative">
              <Search className="absolute left-3 top-1/2 transform -translate-y-1/2 text-slate-400" size={20} />
              <Input
                placeholder="Search members..."
                value={searchQuery}
                onChange={(e) => setSearchQuery(e.target.value)}
                className="pl-10 input-goldsands"
                data-testid="search-members"
              />
            </div>
            <Select value={statusFilter} onValueChange={setStatusFilter}>
              <SelectTrigger className="w-[200px]" data-testid="filter-status">
                <SelectValue placeholder="Filter by status" />
              </SelectTrigger>
              <SelectContent>
                <SelectItem value="all">All Statuses</SelectItem>
                {MEMBER_STATUSES.map(s => (
                  <SelectItem key={s.value} value={s.value}>{s.label}</SelectItem>
                ))}
              </SelectContent>
            </Select>
          </div>
        </CardContent>
      </Card>

      {/* Members Table */}
      <Card className="card-goldsands">
        <Table>
          <TableHeader>
            <TableRow className="bg-slate-50">
              <TableHead>Lead Name</TableHead>
              <TableHead>Email</TableHead>
              <TableHead>Phone</TableHead>
              <TableHead>Status</TableHead>
              <TableHead>Seat Confirmed</TableHead>
              <TableHead>Check-in Time</TableHead>
              {!isReadOnly && <TableHead>Actions</TableHead>}
            </TableRow>
          </TableHeader>
          <TableBody>
            {filteredMembers.map(member => {
              const lead = leads.find(l => l.lead_id === member.lead_id);
              return (
                <TableRow key={member.campaign_member_id} data-testid={`member-row-${member.campaign_member_id}`}>
                  <TableCell className="font-medium">
                    {lead ? `${lead.first_name} ${lead.last_name}` : 'Unknown Lead'}
                  </TableCell>
                  <TableCell>{lead?.email || '-'}</TableCell>
                  <TableCell>{lead?.phone || '-'}</TableCell>
                  <TableCell>
                    {canModifyMember(member) ? (
                      <Select 
                        value={member.status} 
                        onValueChange={(v) => handleUpdateMemberStatus(member.campaign_member_id, v)}
                      >
                        <SelectTrigger className="w-[160px]" data-testid={`status-select-${member.campaign_member_id}`}>
                          <Badge className={getStatusBadgeColor(member.status)}>
                            {getStatusLabel(member.status)}
                          </Badge>
                        </SelectTrigger>
                        <SelectContent>
                          {MEMBER_STATUSES.map(s => (
                            <SelectItem key={s.value} value={s.value}>{s.label}</SelectItem>
                          ))}
                        </SelectContent>
                      </Select>
                    ) : (
                      <Badge className={getStatusBadgeColor(member.status)}>
                        {getStatusLabel(member.status)}
                      </Badge>
                    )}
                  </TableCell>
                  <TableCell>
                    {canModifyMember(member) ? (
                      <Checkbox 
                        checked={member.seat_confirmed}
                        onCheckedChange={() => handleToggleSeatConfirmed(member.campaign_member_id, member.seat_confirmed)}
                        data-testid={`seat-checkbox-${member.campaign_member_id}`}
                      />
                    ) : (
                      member.seat_confirmed ? <CheckCircle size={16} className="text-green-600" /> : '-'
                    )}
                  </TableCell>
                  <TableCell>
                    {member.checkin_time ? format(new Date(member.checkin_time), 'MMM d, h:mm a') : '-'}
                  </TableCell>
                  {!isReadOnly && (
                    <TableCell>
                      {canRemoveMember(member) && (
                        <Button 
                          variant="ghost" 
                          size="icon" 
                          className="text-red-600 hover:text-red-700"
                          onClick={() => handleRemoveMember(member.campaign_member_id)}
                          data-testid={`remove-member-${member.campaign_member_id}`}
                        >
                          <Trash2 size={16} />
                        </Button>
                      )}
                    </TableCell>
                  )}
                </TableRow>
              );
            })}
          </TableBody>
        </Table>
        {filteredMembers.length === 0 && (
          <div className="py-12 text-center">
            <Target className="mx-auto h-12 w-12 text-slate-300 mb-4" />
            <p className="text-slate-500">No campaign members found</p>
            {(canAddOwn || canAddAny) && (
              <Button className="btn-accent mt-4" onClick={() => setIsAddLeadOpen(true)}>
                Add your first lead
              </Button>
            )}
          </div>
        )}
      </Card>
    </div>
  );
}
